import { z } from 'zod';
import { passwordSchema } from './auth.js';
import { paginationQuerySchema } from './pagination.js';
import { permissionOverrideSchema, permissionSchema, roleSchema } from './permissions.js';
import { egyptMobileSchema } from './phone.js';
export const fullNameSchema = z.string().trim().min(2, 'Name must be at least 2 characters').max(100);
export const userDtoSchema = z.object({
    id: z.string(),
    phoneNumber: z.string(),
    fullName: z.string(),
    role: roleSchema,
    isActive: z.boolean(),
    permissions: z.array(permissionSchema),
    permissionOverrides: z.array(permissionOverrideSchema),
    /**
     * The team manager: the user who plans this user's visits (assignVisits sees
     * only the users whose manager they are). Null = in no team.
     */
    managerId: z.string().nullable(),
    managerName: z.string().nullable(),
    lastLoginAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const uniqueOverrides = (overrides) => new Set(overrides.map((o) => o.permission)).size === overrides.length;
export const createUserRequestSchema = z.object({
    phoneNumber: egyptMobileSchema,
    fullName: fullNameSchema,
    password: passwordSchema,
    role: roleSchema,
    permissionOverrides: z.array(permissionOverrideSchema).max(20).refine(uniqueOverrides, 'Duplicate permission override').default([]),
    managerId: z.uuid().nullable().default(null),
});
export const updateUserRequestSchema = z
    .object({
    phoneNumber: egyptMobileSchema,
    fullName: fullNameSchema,
    password: passwordSchema,
    role: roleSchema,
    isActive: z.boolean(),
    /** When present, replaces the full override list. */
    permissionOverrides: z.array(permissionOverrideSchema).max(20).refine(uniqueOverrides, 'Duplicate permission override'),
    managerId: z.uuid().nullable(),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
export const USER_STATUS_FILTERS = ['active', 'inactive', 'all'];
export const listUsersQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().max(100).optional(),
    role: roleSchema.optional(),
    status: z.enum(USER_STATUS_FILTERS).default('all'),
    managerId: z.uuid().optional(),
});
/**
 * Why the server refused a user-management change: sent as `error.details.reason`
 * on the FORBIDDEN / CONFLICT response (the English `message` is unchanged).
 * The permission reasons also carry `details.permissions`: the permissions at issue.
 */
export const USER_ADMIN_REASONS = [
    /** Only a super admin may give the super admin role (FORBIDDEN). */
    'SUPER_ADMIN_ONLY_ASSIGNS_SUPER_ADMIN',
    /** An override grants permissions the actor does not hold (FORBIDDEN, details.permissions). */
    'CANNOT_GRANT_UNHELD_PERMISSIONS',
    /** Role defaults plus overrides would exceed the actor's own permissions (FORBIDDEN, details.permissions). */
    'EXCEEDS_OWN_PERMISSIONS',
    /** FORBIDDEN */
    'CANNOT_DEACTIVATE_SELF',
    /** Your own role or permission overrides (FORBIDDEN). */
    'CANNOT_EDIT_SELF_ROLE',
    /** FORBIDDEN */
    'SUPER_ADMIN_ONLY_MODIFIES_SUPER_ADMIN',
    /** FORBIDDEN */
    'CANNOT_DELETE_SELF',
    /** FORBIDDEN */
    'SUPER_ADMIN_ONLY_DELETES_SUPER_ADMIN',
    /** Demoting, deactivating or deleting the last active super admin (CONFLICT). */
    'LAST_SUPER_ADMIN',
    /** A user cannot be their own team manager (CONFLICT). */
    'OWN_MANAGER',
    /** The chosen manager is managed by this user (CONFLICT). */
    'MANAGER_LOOP',
    /** Another account has this phone number (CONFLICT). */
    'PHONE_IN_USE',
    /** A deleted account has this phone number (CONFLICT). */
    'PHONE_OF_DELETED_USER',
];
export const userAdminReasonSchema = z.enum(USER_ADMIN_REASONS);
//# sourceMappingURL=users.js.map